import type { EvidenceFields } from '../components/Checklist';

export type StepState = 'blocked' | 'open' | 'done' | 'skipped' | 'not_applicable';

export interface StepCell {
  id: string;
  state: StepState;
  dueAt: string | null;
  completedAt: string | null;
  completedVia: string | null;
  overdue: boolean;
  owner: string;
  label: string;
}

export interface StipendRow {
  participantId: string;
  userId: string | null;
  name: string | null;
  email: string | null;
  status: string;
  route: string;
  routeSource: string;
  computedRoute: string | null;
  fellowKind: string | null;
  templateKey: string;
  supervisor: { id: string; name: string; email: string | null } | null;
  signatoryLooksValid: boolean | null;
  agreementId: string | null;
  steps: Record<string, StepCell>;
  waitingOn: string[];
  progress: { total: number; finished: number };
  ledger: { id: string; status: string; amountCents: number | null } | null;
}

export interface ContactRef {
  id: string;
  name: string;
  email: string | null;
  title?: string | null;
}

export interface Agreement {
  id: string;
  groupKey: string;
  recipientKind: 'supervisor' | 'fellow';
  status: string;
  participants: { id: string; name: string | null; email: string | null }[];
  supervisor: ContactRef | null;
  signatory: ContactRef | null;
  adminContact: ContactRef | null;
  institutionName: string | null;
  amountCents: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  batchNumber: number | null;
  batchedAt: string | null;
  note: string | null;
  ready?: boolean;
  form300ReminderDue?: boolean;
}

export interface StepColumn {
  key: string;
  label: string;
  owner: string;
  shared: boolean;
  completion: 'manual' | 'automatic';
}

export interface StipendSettings {
  stipendsEnabled: boolean;
  stipendsEnabledExplicit: boolean;
  stipendAmountCents: number;
  stipendFormUrl: string | null;
}

export interface StipendGrid {
  cycle: { id: string; label: string; year: number | null; settings: StipendSettings };
  columns: Record<string, StepColumn[]>;
  rows: StipendRow[];
  agreements: Agreement[];
  batches: { number: number; agreementIds: string[]; batchedAt: string | null }[];
  routes: Record<string, string>;
  outbox: boolean;
}

export interface StipendCounts {
  countsOnly: true;
  fellows: number;
  byRoute: Record<string, number>;
  complete: number;
  enabled: boolean;
}

export interface NpiPack {
  cycle: { id: string; label: string };
  groups: {
    stepKey: string;
    label: string;
    entries: {
      agreementId: string | null;
      itemIds: string[];
      fellows: (string | null)[];
      supervisor: string | null;
      institution: string | null;
      amountCents: number | null;
      dueAt: string | null;
      overdue: boolean;
    }[];
  }[];
}

export interface PreparedMessage {
  kind: string;
  label: string;
  templateKey: string;
  scope: 'participant' | 'agreement';
  participantId: string | null;
  agreementId: string | null;
  recipients: { email: string; name: string | null; role: string }[];
  preview: { subject: string; text: string } | null;
  problems: string[];
  outbox: boolean;
}

export interface ParticipantStipend {
  participantId: string;
  cycle: { id: string; label: string; year: number | null };
  attached: boolean;
  route: string | null;
  routeSource: string | null;
  computedRoute: string | null;
  fellowKind: string | null;
  hostYork: boolean | null;
  signatoryLooksValid: boolean | null;
  supervisor: ContactRef | null;
  signatory: ContactRef | null;
  adminContact: ContactRef | null;
  periodStart: string | null;
  periodEnd: string | null;
  agreement: Agreement | null;
  ledger: { id: string; status: string; amountCents: number | null; fiscalYear: string } | null;
  routes: Record<string, string>;
}

export const ROUTE_TEMPLATE: Record<string, string> = {
  tof: 'stipend_tof',
  york_student: 'stipend_york_student',
  york_ecr: 'stipend_york_ecr',
};

export const AGREEMENT_STATUS: Record<string, string> = {
  preparing: 'Preparing',
  with_research_services: 'With Research Services',
  executed: 'Executed',
  funded: 'Funded',
  clearing: 'Clearing',
  cleared: 'Cleared',
  dissolved: 'Dissolved',
};

export const WAITING_ON: { value: string; label: string }[] = [
  { value: '', label: 'Everyone' },
  { value: 'program_manager', label: 'Waiting on me (Program Manager)' },
  { value: 'supervisor', label: 'Waiting on supervisor' },
  { value: 'research_services', label: 'Waiting on Research Services' },
  { value: 'npi', label: 'Waiting on the NPI' },
];

export const MESSAGE_LABEL: Record<string, string> = {
  phase1: 'Phase 1 notice',
  form_reminder: 'Form reminder',
  signature_request: 'Signature request',
  form300_reminder: 'Form 300 reminder',
};

export const CELL: Record<StepState, { glyph: string; className: string; label: string }> = {
  blocked: { glyph: '·', className: 'text-ink-3', label: 'Waiting on earlier steps' },
  open: { glyph: '○', className: 'text-ink-2', label: 'Open' },
  done: { glyph: '●', className: 'text-accent', label: 'Done' },
  skipped: { glyph: '–', className: 'text-ink-2', label: 'Skipped' },
  not_applicable: { glyph: 'n/a', className: 'text-ink-3 text-small', label: 'Not applicable' },
};

export const STIPEND_EVIDENCE: EvidenceFields = {
  form300_received: [{ key: 'amountCleared', label: 'Amount cleared ($)', required: true }],
  prior_form300: [
    { key: 'amountCleared', label: 'Amount cleared ($)', required: true },
    { key: 'transferred', label: 'Transferred last year ($)' },
  ],
};
